import PropTypes from 'prop-types';
import React from 'react';
import Slide from '@material-ui/core/Slide';
import { withStyles } from '@material-ui/core/styles';
import { Dialog, DialogTitle } from '../Dialog';
import { ForgotPasswordTab } from '../ForgotPassword';
import { CreatePasswordTab } from '../CreatePassword';
import { ChangePasswordTab } from '../ChangePassword';
import { UpdateEmailTab } from '../UpdateEmailTab';
import { SignInTab } from '../SignInTab';
import { SignUpTab } from '../SignUpTab';
import { LoginSuccessTab } from '../LoginSuccessTab';
import { ConfirmLogoutTab } from '../ConfirmLogoutTab';
import { DefaultSuccessTab } from '../DefaultSuccessTab';
import { withNamespaces } from 'react-i18next';

const TAB_FORGOT_PASSWORD = 'forgotPassword';
const TAB_FORGOT_PASSWORD_SUCCESS = 'forgotPasswordSuccess';
const TAB_SIGN_IN = 'signIn';
const TAB_SIGN_UP = 'signUp';
const TAB_LOGIN_SUCCESS = 'loginSuccess';
const TAB_CHANGE_PASSWORD = 'changePassword';
const TAB_CHANGE_PASSWORD_SUCCESS = 'changePasswordSuccess';
const TAB_UPDATE_EMAIL = 'updateEmail';
const TAB_UPDATE_EMAIL_SUCCESS = 'updateEmailSuccess';
const TAB_CONFIRM_LOGOUT = 'confirmLogout';
const TAB_CREATE_PASSWORD = 'createPassword';
const TAB_CREATE_PASSWORD_SUCCESS = 'createPasswordSuccess';

// most of these styles pertain to common layout styling across
// sign in/sign up/success/resest pages; passed down as props
const styles = (theme) => ({
  body: {
    padding: 0,
    maxWidth: '650px',
    [theme.breakpoints.up('md')]: {
      width: '650px',
      height: '590px',
    },
  },
  smallLayoutBody: {
    maxWidth: '410px',
    [theme.breakpoints.up('md')]: {
      width: '410px',
      height: '215px',
    },
  },
  dialogTitle: {
    borderBottom: 'none !important',
  },
  drawerDivider: {
    marginBottom: theme.spacing.unit / 2,
    marginTop: theme.spacing.unit / 2,
  },
  dialog: {
    margin: 14,
  },
  cancelBtn: {
    margin: '0 auto',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  footer__terms: {
    fontSize: '.875em',
    color: '#4f4f4f',
    maxWidth: '325px',
    margin: '0 auto',
    lineHeight: '20px',
    '& a': {
      fontFamily: 'Pluto Sans Bold',
      color: '#000',
      textDecoration: 'none',
    },
  },
  footer__otherAccountPage: {
    textAlign: 'center',
    width: '100%',
    fontSize: '1em',
    fontFamily: 'Pluto Sans Semibold',
    lineHeight: '24px',
    margin: '10px 0 8px',
    color: '#000',
    '& a': {
      color: '#5e00ff',
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  subtext: {
    color: '#3d3d3d',
    textAlign: 'center',
    fontFamily: 'Pluto Sans Semibold',
    marginTop: 0,
    [theme.breakpoints.up('md')]: {
      fontSize: '1.125em',
      lineHeight: '26px',
    },
    '& span': {
      color: '#5e00ff',
    },
  },
  tabContainer: {
    padding: '0 20px 40px',
    maxWidth: '420px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      padding: '0px',
    },
  },
  title: {
    fontFamily: 'Pluto Sans Bold',
    color: '#000',
    fontSize: '2em',
    margin: '0 0 10px',
    textAlign: 'center',
    textTransform: 'capitalize',
    [theme.breakpoints.up('md')]: {
      fontSize: '3em',
    },
  },
});

const Transition = (props) => <Slide direction="up" {...props} />;

const AuthenticationDialog = ({
  classes,
  errors,
  isAuthenticated,
  user,
  userName,
  onClose,
  provider,
  selectedTab,
  values,
  updatedEmail,
  recoveryEmail,
  logout,
  t,
  ...props
}) => {
  const tabProps = { errors, handleTabChange: props.onTabChange, onClose };

  return (
    <Dialog
      classes={{ paper: classes.dialog }}
      open={props.open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
    >
      <DialogTitle classes={{ dialogTitle: classes.dialogTitle }} onClose={onClose} />
      <div
        className={
          selectedTab === TAB_CONFIRM_LOGOUT
            ? `${classes.body} ${classes.smallLayoutBody}`
            : classes.body
        }
      >
        {selectedTab === TAB_FORGOT_PASSWORD && (
          <ForgotPasswordTab
            {...tabProps}
            onSubmit={props.onForgotPassword}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
              cancelBtn: classes.cancelBtn,
            }}
          />
        )}
        {selectedTab === TAB_FORGOT_PASSWORD_SUCCESS && (
          <DefaultSuccessTab
            {...tabProps}
            title={t('forgot_password_caps', 'Forgot Password').toLowerCase()}
            subtext={t('enter_previous_email', 'Enter the email you signed up with below')}
            message={`${t(
              'password_instructions',
              'Password reset instructions have been sent to',
            )} ${recoveryEmail}`}
            onCloseHandler={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_CREATE_PASSWORD && (
          <CreatePasswordTab
            {...tabProps}
            onSubmit={props.onCreatePassword}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
              cancelBtn: classes.cancelBtn,
            }}
          />
        )}
        {selectedTab === TAB_CREATE_PASSWORD_SUCCESS && (
          <DefaultSuccessTab
            {...tabProps}
            title={t('create_password', 'Create Password')}
            subtext={t(
              'create_password_subtext',
              'Enter the email you signed up with below to receive instructions on how to create a new password.',
            )}
            message={`${t(
              'create_password_success',
              'Password instructions have been sent to',
            )} ${recoveryEmail}`}
            onCloseHandler={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_SIGN_IN && (
          <SignInTab
            {...tabProps}
            onSubmit={props.onSignIn}
            openDialog={props.openDialog}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
              footer: classes.footer,
              footer__terms: classes.footer__terms,
              footer__otherAccountPage: classes.footer__otherAccountPage,
              submitWrapper: classes.submitWrapper,
            }}
          />
        )}
        {selectedTab === TAB_SIGN_UP && (
          <SignUpTab
            {...tabProps}
            onSubmit={props.onSignUp}
            provider={provider}
            values={values}
            openDialog={props.openDialog}
            generalErrorMsg={props.generalErrorMsg}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
              footer: classes.footer,
              footer__terms: classes.footer__terms,
              footer__otherAccountPage: classes.footer__otherAccountPage,
              submitWrapper: classes.submitWrapper,
            }}
          />
        )}
        {selectedTab === TAB_LOGIN_SUCCESS && (
          <LoginSuccessTab
            {...tabProps}
            userName={userName}
            onClose={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_CHANGE_PASSWORD && (
          <ChangePasswordTab
            {...tabProps}
            onSubmit={props.onChangePassword}
            onClose={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
            }}
          />
        )}
        {selectedTab === TAB_CHANGE_PASSWORD_SUCCESS && (
          <DefaultSuccessTab
            {...tabProps}
            title={t('change_password', 'Change Password')}
            subtext=""
            message={`${t('changed_password', 'Your password has been successfully changed')}.`}
            onCloseHandler={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_UPDATE_EMAIL && (
          <UpdateEmailTab
            {...tabProps}
            onSubmit={props.onUpdateEmail}
            onClose={onClose}
            user={user}
            updatedEmail={updatedEmail}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_UPDATE_EMAIL_SUCCESS && (
          <DefaultSuccessTab
            {...tabProps}
            title={t('update_email', 'Update Email')}
            subtext={[
              t('current_email', 'Current email'),
              ': ',
              <span>{`${updatedEmail}`}</span>,
              '',
            ]}
            message={`${t('changed_email_success', 'Your email has been successfully changed')}.`}
            user={user}
            onCloseHandler={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
        {selectedTab === TAB_CONFIRM_LOGOUT && (
          <ConfirmLogoutTab
            {...tabProps}
            logout={logout}
            onClose={onClose}
            classes={{
              tabContainer: classes.tabContainer,
              title: classes.title,
              subtext: classes.subtext,
            }}
          />
        )}
      </div>
    </Dialog>
  );
};

AuthenticationDialog.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onForgotPassword: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  onSignUp: PropTypes.func.isRequired,
  onSocialAuth: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  provider: PropTypes.string,
  selectedTab: PropTypes.string,
  values: PropTypes.objectOf(PropTypes.string),
  openDialog: PropTypes.func.isRequired,
  onUpdateEmail: PropTypes.func.isRequired,
};
AuthenticationDialog.defaultProps = {
  provider: null,
  selectedTab: null,
  values: null,
};

export const AuthenticationDialogComponent = withStyles(styles)(
  withNamespaces()(AuthenticationDialog),
);
